import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Volume2, VolumeX, ChevronDown } from "lucide-react";
import { useContent } from "@/hooks/useContent";

// Total frames extracted from the two hero films (travel -> celebration)
const TOTAL = 240;
const pad = (n) => String(n).padStart(3, "0");

// Fallback story chapters (text overridable from admin content)
const CHAPTERS = [
  { at: 0.02, eyebrow: "Chapter I — Dawn", title: "Every experience\nbegins somewhere.", sub: "" },
  { at: 0.16, eyebrow: "Chapter II — The Journey", title: "Go somewhere.", sub: "Curated journeys. Thoughtfully planned." },
  { at: 0.34, eyebrow: "Chapter III — Arrival", title: "Feel the\ndestination.", sub: "Landscapes, architecture, atmosphere." },
  { at: 0.5, eyebrow: "Chapter IV — The Details", title: "The details matter.", sub: "Everything considered. Nothing left for you to worry about." },
  { at: 0.66, eyebrow: "Chapter V — Golden Hour", title: "Travel becomes\ncelebration.", sub: "" },
  { at: 0.8, eyebrow: "Event Management — a new vertical", title: "You bring the occasion.\nWe handle the experience.", sub: "" },
  { at: 0.95, eyebrow: "VacanzaGo", title: "Travel · Events\n· Experiences", sub: "You don't have to worry about anything once we take over." },
];

function useIsMobile() {
  const [m, setM] = useState(typeof window !== "undefined" ? window.innerWidth < 768 : false);
  useEffect(() => {
    const on = () => setM(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return m;
}

export function CinematicHero() {
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const rafRef = useRef(0);
  const targetProgress = useRef(0);
  const currentProgress = useRef(0);
  const audioRef = useRef(null);
  const isMobile = useIsMobile();
  const [loaded, setLoaded] = useState(0);
  const [ready, setReady] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [chapterIdx, setChapterIdx] = useState(0);
  const [soundOn, setSoundOn] = useState(false);
  const content = useContent();
  const chapters = CHAPTERS.map((d, i) =>
    content.hero_chapters && content.hero_chapters[i] ? { ...d, ...content.hero_chapters[i] } : d
  );

  const dir = isMobile ? "mobile" : "desktop";

  // Preload frames
  useEffect(() => {
    const rm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setReduced(rm);
    let count = 0;
    const imgs = new Array(TOTAL + 1);
    let cancelled = false;
    for (let i = 1; i <= TOTAL; i++) {
      const img = new Image();
      img.src = `/hero/${dir}/f_${pad(i)}.webp`;
      img.onload = img.onerror = () => {
        if (cancelled) return;
        count += 1;
        setLoaded(count);
        if (i === 1) drawFrame(1);
        if (count >= TOTAL) setReady(true);
      };
      imgs[i] = img;
    }
    imagesRef.current = imgs;
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dir]);

  const drawFrame = useCallback((frame) => {
    const canvas = canvasRef.current;
    const img = imagesRef.current[Math.max(1, Math.min(TOTAL, Math.round(frame)))];
    if (!canvas || !img || !img.complete || !img.naturalWidth) return;
    const ctx = canvas.getContext("2d");
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = canvas.clientWidth, ch = canvas.clientHeight;
    if (canvas.width !== cw * dpr || canvas.height !== ch * dpr) {
      canvas.width = cw * dpr; canvas.height = ch * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // cover fit
    const ir = img.naturalWidth / img.naturalHeight;
    const cr = cw / ch;
    let dw, dh, dx, dy;
    if (cr > ir) { dw = cw; dh = cw / ir; dx = 0; dy = (ch - dh) / 2; }
    else { dh = ch; dw = ch * ir; dx = (cw - dw) / 2; dy = 0; }
    ctx.drawImage(img, dx, dy, dw, dh);
  }, []);

  // Scroll -> target progress
  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const scrollable = el.offsetHeight - window.innerHeight;
      const p = Math.max(0, Math.min(1, -rect.top / scrollable));
      targetProgress.current = p;
      // chapter
      let idx = 0;
      for (let i = 0; i < CHAPTERS.length; i++) if (p >= CHAPTERS[i].at) idx = i;
      setChapterIdx(idx);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Smooth interpolation render loop
  useEffect(() => {
    const loop = () => {
      const t = targetProgress.current;
      currentProgress.current += (t - currentProgress.current) * 0.12;
      if (reduced) currentProgress.current = t;
      const frame = 1 + currentProgress.current * (TOTAL - 1);
      drawFrame(frame);
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [drawFrame, reduced]);

  // redraw on resize
  useEffect(() => {
    const on = () => drawFrame(1 + currentProgress.current * (TOTAL - 1));
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, [drawFrame]);

  const toggleSound = () => {
    const a = audioRef.current;
    if (!a) return;
    if (soundOn) { a.pause(); setSoundOn(false); }
    else { a.volume = 0.4; a.play().catch(() => {}); setSoundOn(true); }
  };

  const pct = Math.round((loaded / TOTAL) * 100);

  return (
    <section ref={sectionRef} className="relative" style={{ height: "560vh" }} data-testid="cinematic-hero">
      <div className="sticky top-0 h-screen w-full overflow-hidden bg-[#1a0f02]">
        <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
        {/* readability scrims */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(26,15,2,0.55) 0%, rgba(26,15,2,0.12) 38%, rgba(26,15,2,0.3) 70%, rgba(26,15,2,0.82) 100%)" }} />

        {/* loading */}
        {!ready && pct < 100 && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1a0f02] z-30" data-testid="hero-loader">
            <div className="font-display text-4xl text-[#F49E1A]">VacanzaGo</div>
            <div className="mt-6 h-[2px] w-48 bg-white/15 overflow-hidden">
              <div className="h-full bg-[#F49E1A] transition-all duration-200" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-3 text-xs uppercase tracking-[0.3em] text-white/40">Preparing the journey</p>
          </div>
        )}

        {/* chapter text */}
        <div className="absolute inset-0 flex items-center justify-center px-6 pointer-events-none">
          <div key={chapterIdx} className="max-w-3xl text-center hero-chapter">
            <p className="eyebrow text-[#F49E1A] mb-4">{chapters[chapterIdx].eyebrow}</p>
            <h1 className="font-display text-white font-medium tracking-tight leading-[1.02] text-4xl sm:text-6xl lg:text-7xl whitespace-pre-line" style={{ textShadow: "0 2px 40px rgba(0,0,0,0.5)" }}>
              {chapters[chapterIdx].title}
            </h1>
            {chapters[chapterIdx].sub && (
              <p className="mt-5 text-base sm:text-lg text-[#F9EFE1]/90 font-light max-w-xl mx-auto">{chapters[chapterIdx].sub}</p>
            )}
          </div>
        </div>

        {/* progress line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-white/10 z-20">
          <div className="h-full bg-[#F49E1A]" style={{ width: `${Math.round(currentProgress.current * 100)}%`, transition: "width 0.1s linear" }} />
        </div>

        {/* sound toggle */}
        <button onClick={toggleSound} data-testid="hero-sound-toggle" aria-label="Toggle sound"
          className="absolute top-24 right-5 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-black/20 text-white/80 backdrop-blur-sm hover:bg-black/40 transition-colors">
          {soundOn ? <Volume2 size={17} /> : <VolumeX size={17} />}
        </button>

        {/* CTAs */}
        <div className="absolute bottom-10 left-0 right-0 z-20 flex flex-col items-center gap-5 px-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/packages" data-testid="hero-explore-travel" className="rounded-full btn-orange px-7 py-3 text-sm">Explore Travel</Link>
            <Link to="/events" data-testid="hero-plan-event" className="rounded-full border border-white/50 text-white px-7 py-3 text-sm hover:bg-white/10 transition-colors">Plan an Event</Link>
            <Link to="/packages" data-testid="hero-plan-journey" className="rounded-full border border-white/50 text-white px-7 py-3 text-sm hover:bg-white/10 transition-colors">Plan My Journey</Link>
          </div>
          <div className="flex items-center gap-2 text-white/55 text-xs">
            <ChevronDown size={16} className="animate-bounce" /> Scroll to move through the film
          </div>
        </div>
      </div>

      {/* atmospheric audio (off by default) */}
      <audio ref={audioRef} loop preload="none" src="https://cdn.pixabay.com/download/audio/2022/03/15/audio_c8e979e0f3.mp3?filename=cinematic-ambient-piano-110241.mp3" />
    </section>
  );
}
